import type {
  Diagnostic,
  LossReport,
  ModelDocument,
} from "../shared/contracts.js";
import {
  createModel,
  validateModel,
  assertSafeValue,
  normalizeModel,
} from "../core/model.js";
const loss = (): LossReport => ({
  preserved: [],
  translated: [],
  preservedOpaque: [],
  lost: [],
  unresolved: [],
});
export function importNative(text: string): {
  document: ModelDocument;
  diagnostics: Diagnostic[];
  lossReport: LossReport;
} {
  const lossReport = loss();
  const rejected = (code: string, message: string) => ({
    document: createModel("Rejected import"),
    diagnostics: [
      { code, severity: "error" as const, message, helpTopic: "native-import" },
    ],
    lossReport: { ...lossReport, unresolved: [message] },
  });
  if (text.length > 50 * 1024 * 1024)
    return rejected(
      "NATIVE_SIZE_LIMIT",
      "Native model text exceeds the 50 MiB character budget.",
    );
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
    assertSafeValue(parsed);
  } catch (e) {
    return rejected(
      "NATIVE_INVALID_JSON",
      `Native import rejected: ${(e as Error).message}`,
    );
  }
  let document: ModelDocument;
  let diagnostics: Diagnostic[];
  try {
    document = normalizeModel(parsed);
    diagnostics = validateModel(document);
  } catch (e) {
    const details = (e as { details?: unknown }).details;
    return {
      ...rejected("NATIVE_INVALID_MODEL", (e as Error).message),
      ...(Array.isArray(details)
        ? { diagnostics: details as Diagnostic[] }
        : {}),
    };
  }
  const original = parsed as ModelDocument;
  if (
    original.manifest.objectCount !== original.objects.length ||
    original.manifest.relationshipCount !== original.relationships.length
  )
    diagnostics.push({
      code: "NATIVE_MANIFEST_COUNTS",
      severity: "error",
      message: "Manifest counts do not match model contents.",
    });
  if (
    original.model.activeRepresentation !==
      document.model.activeRepresentation ||
    original.diagrams.length !== document.diagrams.length
  )
    lossReport.translated.push(
      "Canonical symbolic representation normalized and missing presentation initialized; semantic object identities preserved.",
    );
  const foreignRefs = document.mappings
    .flatMap((m) => [...m.sourceRefs, ...m.destinationRefs])
    .filter((r) => r.modelId !== document.model.modelId);
  if (foreignRefs.length) {
    lossReport.unresolved.push(
      `${foreignRefs.length} external mapping references require authorized destination binding.`,
    );
    diagnostics.push({
      code: "NATIVE_EXTERNAL_BINDINGS",
      severity: "warning",
      message:
        "External model references are preserved; destination authorization and version resolution remain required.",
    });
  }
  lossReport.preserved.push(
    `${document.objects.length} canonical objects`,
    `${document.relationships.length} relationships`,
    "Representations, diagrams, mappings and extensions",
  );
  return { document, diagnostics, lossReport };
}
export function exportNative(document: ModelDocument): string {
  const diagnostics = validateModel(document);
  if (diagnostics.some((d) => d.severity === "error")) {
    const error = new Error(
      "Cannot export an invalid canonical model.",
    ) as Error & { code: string; diagnostics: Diagnostic[] };
    error.code = "NATIVE_INVALID_MODEL";
    error.diagnostics = diagnostics;
    throw error;
  }
  // Arbitrary user annotations may be sensitive; export approval and tenant policy are enforced by caller.
  // Explicit credential-bearing keys are never allowed into an ordinary portable model artifact.
  const forbidden =
    /^(?:password|accessToken|refreshToken|clientSecret|privateKey|connectionString|apiKey)$/i;
  const walk = (value: unknown, path: string) => {
    if (value && typeof value === "object")
      for (const [key, child] of Object.entries(value)) {
        if (forbidden.test(key) && typeof child === "string" && child !== "") {
          const err = new Error(
            `Remove credential-bearing property before export: ${path}.${key}`,
          ) as Error & { code: string };
          err.code = "NATIVE_SECRET_FIELD";
          throw err;
        }
        walk(child, `${path}.${key}`);
      }
  };
  walk(document, "$");
  const copy = structuredClone(document);
  copy.manifest.objectCount = copy.objects.length;
  copy.manifest.relationshipCount = copy.relationships.length;
  return JSON.stringify(copy, null, 2) + "\n";
}
function stable(value: unknown): string {
  if (Array.isArray(value)) return "[" + value.map(stable).join(",") + "]";
  if (value && typeof value === "object")
    return (
      "{" +
      Object.entries(value)
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => JSON.stringify(k) + ":" + stable(v))
        .join(",") +
      "}"
    );
  return JSON.stringify(value) ?? "null";
}
export function compareModels(
  before: ModelDocument,
  after: ModelDocument,
): {
  kind: "added" | "removed" | "changed";
  objectId: string;
  name: string;
  details: string;
}[] {
  const result: {
    kind: "added" | "removed" | "changed";
    objectId: string;
    name: string;
    details: string;
  }[] = [];
  const content = (doc: ModelDocument) =>
    new Map<string, Record<string, unknown>>([
      ...doc.objects.map(
        (o) => [o.objectId, o as unknown as Record<string, unknown>] as const,
      ),
      ...doc.relationships.map(
        (r) =>
          [r.relationshipId, r as unknown as Record<string, unknown>] as const,
      ),
      ...doc.mappings.map(
        (m) => [m.mappingId, m as unknown as Record<string, unknown>] as const,
      ),
      ...doc.representations.map(
        (r) =>
          [
            r.representationId,
            Object.fromEntries(
              Object.entries(r).filter(
                ([k]) => !["createdAt", "objectIds"].includes(k),
              ),
            ),
          ] as const,
      ),
    ]);
  const left = content(before),
    right = content(after);
  for (const [id, o] of left) {
    const other = right.get(id);
    const name = String(o.name ?? o.mappingKind ?? id);
    if (!other) {
      result.push({
        kind: "removed",
        objectId: id,
        name,
        details: `Remove ${o.kind ?? "relationship or mapping"}.`,
      });
      continue;
    }
    // Presentation and provenance capture times do not change semantic intent.
    const compared = (value: unknown, key: string) =>
      key === "sourceIdentities" && Array.isArray(value)
        ? value.map((v) =>
            Object.fromEntries(
              Object.entries(v).filter(([k]) => k !== "observedAt"),
            ),
          )
        : value;
    const keys = new Set([...Object.keys(o), ...Object.keys(other)]);
    const changes = [...keys].filter(
      (k) => stable(compared(o[k], k)) !== stable(compared(other[k], k)),
    );
    if (changes.length)
      result.push({
        kind: "changed",
        objectId: id,
        name: String(other.name ?? name),
        details: changes
          .sort()
          .map((k) => `${k}: ${stable(o[k])} → ${stable(other[k])}`)
          .join("\n"),
      });
  }
  for (const [id, o] of right)
    if (!left.has(id))
      result.push({
        kind: "added",
        objectId: id,
        name: String(o.name ?? o.mappingKind ?? id),
        details: `Add ${o.kind ?? "relationship or mapping"}.`,
      });
  const modelKeys = [
    "name",
    "family",
    "modelType",
    "targetProfile",
    "description",
    "namingPolicy",
    "originalConceptualBaseline",
  ] as const;
  const modelChanges = modelKeys
    .filter((k) => stable(before.model[k]) !== stable(after.model[k]))
    .map((k) => `${k}: ${stable(before.model[k])} → ${stable(after.model[k])}`);
  for (const k of ["dependencies", "extensions"] as const)
    if (stable(before[k]) !== stable(after[k]))
      modelChanges.push(`${k}: ${stable(before[k])} → ${stable(after[k])}`);
  if (modelChanges.length)
    result.push({
      kind: "changed",
      objectId: after.model.modelId,
      name: after.model.name,
      details: modelChanges.join("\n"),
    });
  return result.sort(
    (a, b) =>
      a.name.localeCompare(b.name) || a.objectId.localeCompare(b.objectId),
  );
}
